import { Box, Typography } from '@mui/material';
import { FormShowTemplate, FormFieldsData, useFormShowContext } from 'framework/form';
import { useEmployeeId } from 'domain/people/employee/employeeIdentityRouting';
import useEmployeePositionSlotService from './useEmployeePositionSlotService';
import { useShowContext } from 'framework/show';
import { Alert } from 'framework/ui/mui-builder';
import PositionSlotInput from 'domain/people/positionSlots/input/PositionSlotInput';
import EditFormShowButton from 'domain/people/employee/shared/button/showEdit/EditFormShowButton';
import FormActionButtons from 'domain/people/employee/shared/button/showEdit/FormActionButtons';
import { POSITION_SLOT_ASSIGN } from 'domain/people/positionSlots/constants';
import { useAuthorization } from 'framework/authorization';
import { POSITION_SLOT_LOAD_STATUS } from './positionSlotFormData';
import useEmployeePositionSlotValidationErrors from 'domain/people/positionSlots/useEmployeePositionSlotValidationErrors';
import PositionSlotValidationWarningIcon from 'domain/people/positionSlots/field/PositionSlotValidationWarningIcon';
import { usePositionSlotsUrl } from 'domain/shared/positionSlot';
import { SimpleLinkField } from 'framework/ViewTypes';
import useFeatureToggle from 'framework/featureToggle';
import { Features } from 'infrastructure/featureToggle';

type EmployeePositionSlotFieldProps = {
  onPositionSlotIdChange?: (positionSlotId: string | null) => void;
};

const EmployeePositionSlotFieldContent = () => {
  const { isEditMode } = useFormShowContext();
  const { data } = useShowContext();
  const { isGranted } = useAuthorization();
  const canAssign = isGranted(POSITION_SLOT_ASSIGN);
  const { getSingleEntityUrl } = usePositionSlotsUrl();
  const { isFeatureEnabled } = useFeatureToggle();
  const loadStatus = data?.getValueBySource('loadStatus');
  const employeeAlias = data?.getValueBySource('employeeAlias');
  const positionSlotId = data?.getValueBySource('positionSlotId');
  const positionSlotSummary = data?.getValueBySource('positionSlotSummary');
  const validationErrors = useEmployeePositionSlotValidationErrors(
    employeeAlias,
    positionSlotId,
    loadStatus === POSITION_SLOT_LOAD_STATUS.SUCCESS,
  );

  if (isEditMode) {
    return (
      <Box sx={{ mt: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <FormActionButtons sxWrapper={{ justifyContent: 'flex-end', ml: 0 }} />
        </Box>
        <PositionSlotInput source="assignPositionSlotId" label="Position slot" />
      </Box>
    );
  }

  if (loadStatus === POSITION_SLOT_LOAD_STATUS.ERROR) {
    return (
      <Box sx={{ mt: 2 }}>
        <Alert severity="error">Position slot could not be loaded.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2, position: 'relative', pr: canAssign ? 5 : 0 }}>
      {canAssign && (
        <Box sx={{ position: 'absolute', right: '-15px', top: 0 }}>
          <EditFormShowButton requiredPermission={POSITION_SLOT_ASSIGN} />
        </Box>
      )}
      <Typography variant="body2" color="textSecondary" mb={0.5}>
        Position slot
      </Typography>
      {positionSlotId ? (
        <Box gap={1} display="flex" alignItems="center">
          {isFeatureEnabled(Features.POSITION_SLOTS_COLLECTION_PREVIEW) ? (
            <SimpleLinkField to={getSingleEntityUrl(positionSlotId)} value={positionSlotSummary} />
          ) : (
            <Typography variant="body1">{positionSlotSummary}</Typography>
          )}

          <PositionSlotValidationWarningIcon errors={validationErrors ?? []} />
        </Box>
      ) : (
        <Alert severity="warning">Position slot is not assigned. It should be assigned manually.</Alert>
      )}
    </Box>
  );
};

const EmployeePositionSlotField = ({ onPositionSlotIdChange }: EmployeePositionSlotFieldProps) => {
  const employeeAlias = useEmployeeId();
  const {
    getEmployeePositionSlotFormData,
    assignEmployeeToPositionSlot,
    getEmployeePositionSlotAssignValidationSchema,
  } = useEmployeePositionSlotService();

  if (!employeeAlias) {
    return null;
  }

  const handleSubmit = async (values: FormFieldsData) => {
    const assignPositionSlotId = values.assignPositionSlotId as string;
    const currentPositionSlotId = (values.currentPositionSlotId as string | null) ?? null;

    const formData = await assignEmployeeToPositionSlot(employeeAlias, assignPositionSlotId, currentPositionSlotId);
    onPositionSlotIdChange?.(formData.positionSlotId);

    return formData;
  };

  return (
    <FormShowTemplate
      getDataFunc={async () => {
        const formData = await getEmployeePositionSlotFormData(employeeAlias);
        onPositionSlotIdChange?.(formData.positionSlotId);

        return {
          ...formData,
          currentPositionSlotId: formData.positionSlotId,
          assignPositionSlotId: null,
        };
      }}
      getValidationSchemaFunc={getEmployeePositionSlotAssignValidationSchema}
      submitFormFunc={handleSubmit}
      afterSubmit={{
        message: 'Position slot has been successfully updated.',
      }}
      isSmallLoader
    >
      <EmployeePositionSlotFieldContent />
    </FormShowTemplate>
  );
};

export default EmployeePositionSlotField;
